// Route ideas for the next long ride: three from the library, ranked against what the plan asks
// that week, whether it fits the light and the walls, the wind (a headwind out means a tailwind
// home) and how long since he rode it. Tapping one puts it on the plan as proposed; the diary
// only changes when he books. Pure apart from the wind, which the screen passes in.
import type { State, PlanDay, Route } from "./types";
import { addDays, dow, daysBetween, mondayOf } from "./dates.ts";
import { wallsFor, routeOptions } from "./day.ts";
import { toMin } from "./sun.ts";
import { hav } from "./gpx.ts";

export type Idea = { id: string; name: string; km: number; gain: number; start: number; end: number; planned: boolean; reasons: string[]; score: number };
export type IdeaDay = { date: string; day: PlanDay | null; start: string; target: number | null; ideas: Idea[] };
export type Wind = { from: number; kmh: number };

/** The next weekend day within ten days that has a ride planned, else the next Saturday (to add one). */
export function ideaDay(s: State, today: string): { date: string; day: PlanDay | null } | null {
  const days = s.plan.flatMap((w) => w.days);
  for (let i = 0; i <= 10; i++) {
    const d = addDays(today, i), w = dow(d);
    if (w !== 0 && w !== 6) continue;
    const p = days.find((x) => x.date === d);
    if (p?.type === "ride" && p.status !== "cancelled" && p.status !== "done" && p.start && p.end && !s.rides.some((r) => r.d === d && r.km >= 15)) return { date: d, day: p };
  }
  for (let i = 0; i <= 7; i++) {
    const d = addDays(today, i);
    if (dow(d) !== 6) continue;
    const p = days.find((x) => x.date === d);
    if (p && p.type === "kept") return null;
    if (!p || p.type === "rest") return { date: d, day: p ?? null };
  }
  return null;
}

/** Compass bearing in degrees from a to b. */
const bearing = (a: [number, number], b: [number, number]) => {
  const r = Math.PI / 180, y = Math.sin((b[1] - a[1]) * r) * Math.cos(b[0] * r);
  const x = Math.cos(a[0] * r) * Math.sin(b[0] * r) - Math.sin(a[0] * r) * Math.cos(b[0] * r) * Math.cos((b[1] - a[1]) * r);
  return (Math.atan2(y, x) / r + 360) % 360;
};
/** Which way the ride goes out: from the start to the point furthest from it. */
export function outBearing(track: [number, number][]): number | null {
  if (track.length < 3) return null;
  let far = track[0], fd = 0;
  for (const p of track) { const d = hav(track[0], p); if (d > fd) { fd = d; far = p; } }
  return fd < 3 ? null : bearing(track[0], far);
}
const COMPASS = ["north", "north-east", "east", "south-east", "south", "south-west", "west", "north-west"];
export const compass = (deg: number) => COMPASS[Math.round(deg / 45) % 8];

/** Out into the wind, home with it behind you: the right way round for a long day. */
export function windLine(out: number | null, w: Wind | null): { line: string; plus: number } | null {
  if (out == null || !w || w.kmh < 12) return null;
  const diff = Math.abs(((out - w.from + 540) % 360) - 180); // 0: riding straight into it on the way out
  if (diff <= 60) return { line: `Headwind out, ${w.kmh} km/h from the ${compass(w.from)}. Tailwind home`, plus: 0.15 };
  if (diff >= 120) return { line: `Tailwind out, ${w.kmh} km/h from the ${compass(w.from)}. Hard on the way home`, plus: -0.15 };
  return null;
}

/** When he last rode it: a planned day on that route with a Strava ride, or a ride named after it. */
export function lastRidden(s: State, r: Route, today: string): string | null {
  const real = s.rides.filter((x) => x.km >= 15 && x.d <= today);
  const viaPlan = s.plan.flatMap((w) => w.days).filter((d) => d.route === r.id && real.some((x) => x.d === d.date)).map((d) => d.date);
  // The full name first; the short name ("Brickworks") only when no other route shares it.
  const full = r.n.toLowerCase(), key = full.replace(/\s*\(.*\)\s*/, "").trim();
  const shared = s.routes.filter((x) => x.n.toLowerCase().replace(/\s*\(.*\)\s*/, "").trim() === key).length > 1;
  const viaName = real.filter((x) => x.name && (x.name.toLowerCase().includes(full) || (!shared && x.name.toLowerCase().includes(key)))).map((x) => x.d);
  return [...viaPlan, ...viaName].sort().pop() ?? null;
}

/** outs: each route's way out in degrees, from its imported GPX (routes without one have no wind line). */
export function routeIdeas(s: State, today: string, outs: Record<string, number | null> = {}, wind: Wind | null = null): IdeaDay | null {
  const at = ideaDay(s, today);
  if (!at) return null;
  const start = at.day?.start ?? "09:00";
  const end = at.day?.end ?? "10:30";
  const ramp = s.ramp.find((w) => w.w === mondayOf(at.date));
  const longR = ramp?.long ? s.routes.find((r) => r.id === ramp.long) : undefined;
  const plannedR = at.day?.route ? s.routes.find((r) => r.id === at.day!.route) : undefined;
  const target = longR?.km ?? plannedR?.km ?? null;
  const walls = wallsFor(s, at.date);
  const opts = routeOptions(s, at.date, toMin(start), toMin(end), walls).filter((o) => o.id !== "city-loop" && o.fits);
  const ideas: Idea[] = [];
  for (const o of opts) {
    const r = s.routes.find((x) => x.id === o.id)!;
    if (!r.weekend) continue;
    const reasons: string[] = [];
    let score = 0;
    if (target) {
      const off = (r.km - target) / target;
      score -= Math.abs(off) * 2;
      reasons.push(Math.abs(off) <= 0.1 ? `Right for the ${Math.round(target)} km the plan asks` : off > 0 ? `${Math.round(r.km - target)} km over the ${Math.round(target)} the plan asks` : `${Math.round(target - r.km)} km short of the ${Math.round(target)} the plan asks`);
    }
    const w = windLine(outs[r.id] ?? null, wind);
    if (w) { reasons.push(w.line); score += w.plus; }
    const last = lastRidden(s, r, today);
    if (last) { const wk = Math.floor(daysBetween(last, today) / 7); reasons.push(wk < 1 ? "Ridden this week" : `Last ridden ${wk} ${wk === 1 ? "week" : "weeks"} ago`); score += Math.min(0.2, wk * 0.03); }
    if (r.gain >= 700) { reasons.push(`${r.gain.toLocaleString("en-GB")} m of climbing toward Day 1's 1,553`); score += 0.05; }
    ideas.push({ id: r.id, name: r.n, km: r.km, gain: r.gain, start: o.start, end: o.end, planned: r.id === at.day?.route, reasons, score });
  }
  ideas.sort((a, b) => b.score - a.score);
  // The planned route always shows, so the card says what is on now.
  const top = ideas.slice(0, 3);
  const p = ideas.find((i) => i.planned);
  if (p && !top.includes(p)) top[2] = p;
  return { date: at.date, day: at.day, start, target, ideas: top };
}
